import React, { ChangeEvent, Dispatch, DragEvent, SetStateAction, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import { Upload, X } from 'lucide-react';
import { toast } from '../../../../ui/toast';
import { Button } from '../../../../ui';
import { putJob, uploadFile } from '../../../../utils/api_service/job_api';
import { formatDateTime } from '../../../../utils/helpers/time';
import { ContextBasedGeneratorJob, Settings } from '../../../../utils/types/content_manager_types';
import { GenerateProperties } from '../GenerateProperties/GenerateProperties';
import { SessionTitle } from '../session_title';
import { TopicInput } from '../topic_input';

type UploadDocumentDropboxProps = {
  uploadedFile: File | null;
  setUploadedFile: Dispatch<SetStateAction<File | null>>;
  pollJobStatus: (id: string) => void;
  setJob: Dispatch<SetStateAction<ContextBasedGeneratorJob>>;
  setQuestionLoading: Dispatch<SetStateAction<boolean>>;
  questionLoading: boolean;
  job: ContextBasedGeneratorJob;
};

export const UploadDocumentDropbox: React.FC<UploadDocumentDropboxProps> = ({
  uploadedFile,
  setUploadedFile,
  pollJobStatus,
  setJob,
  setQuestionLoading,
  questionLoading,
  job,
}) => {
  const [topic, setTopic] = useState<string>('');
  const [defaultSessionTitle, setDefaultSessionTitle] = useState<string>(
    `${topic ? topic : ''}-`,
  );
  const [sessionTitle, setSessionTitle] = useState<string>(defaultSessionTitle);
  const [isDragActive, setIsDragActive] = useState<boolean>(false);

  const [editGenerateProperties, setEditGenerateProperties] = useState<Settings>({
    level: '',
    jobTitle: '',
    questionType: '',
    time: 0,
    points: 0,
  });

  const dragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const dragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const dragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const fileDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const files = e.dataTransfer.files;
    if (files.length) {
      handleFiles(files);
    }
  };

  const addFileViaInput = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files?.length) {
      handleFiles(files);
    }
  };

  const handleFiles = (files: FileList) => {
    for (let i = 0; i < files.length; i++) {
      if (validateFile(files[i])) {
        setUploadedFile(files[i]);
      } else {
        toast.error('File type not permitted', { position: 'bottom-center' });
      }
    }
  };

  const validateFile = (file: File) => {
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'application/msword',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/bmp',
      'image/webp',
      'text/html',
      'text/plain',
    ];
    return validTypes.indexOf(file.type) !== -1;
  };

  const editProperties = (
    job_id: string,
    response_job: ContextBasedGeneratorJob,
  ) => {
    const updatedJobWithSettings = {
      ...response_job,
      settings: editGenerateProperties,
    };
    putJob(job_id, updatedJobWithSettings).then((response) => {
      setJob(response);
    });
  };

  const fileSize = (size: number) => {
    if (size === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const doUploadFile = () => {
    setQuestionLoading(true);
    if (uploadedFile !== null && topic !== undefined) {
      uploadFile(uploadedFile, topic, sessionTitle)
        .then((response) => {
          pollJobStatus(response.id);
          editProperties(response.id, response);
        })
        .catch(() => {
          toast.error('Error. Please try again', { position: 'bottom-center' });
          setQuestionLoading(false);
        });
    }
  };

  const acceptedFormats =
    'DOCX, PDF, PPT, DOC, JPEG, PNG, GIF, BMP, WEBP, HTML, TXT';

  return (
    <Stack spacing={2}>
      <SessionTitle
        sessionTitle={sessionTitle}
        setSessionTitle={setSessionTitle}
        defaultValue={defaultSessionTitle}
        disabled={questionLoading}
      />

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
            Content Topic
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Enter one main skill, then optional sub-skills with Enter.
          </Typography>
        </Box>
        <TopicInput
          setTopic={setTopic}
          disabled={questionLoading}
          placeholder="Type a skill and press Enter..."
        />
      </Paper>

      <GenerateProperties
        setEditGenerateProperties={setEditGenerateProperties}
        job={job}
      />

      <input
        type="file"
        onChange={addFileViaInput}
        id="upload-file-input"
        style={{ display: 'none' }}
      />

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { md: 'center' },
            gap: 1.5,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
              Upload Document
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Auto-generate questions by uploading a source document.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip
              label="Max file size: 2 GB"
              size="small"
              sx={{ fontWeight: 600, fontSize: '0.6875rem' }}
            />
            <Chip
              label={`Formats: ${acceptedFormats}`}
              size="small"
              sx={{ fontWeight: 600, fontSize: '0.6875rem' }}
            />
          </Stack>
        </Box>

        <Box
          sx={{
            mt: 2,
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 300px' },
          }}
        >
          <Box
            onDragOver={dragOver}
            onDragEnter={dragEnter}
            onDragLeave={dragLeave}
            onDrop={fileDrop}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'divider',
              bgcolor: isDragActive ? 'rgba(16, 185, 129, 0.05)' : 'background.paper',
              textAlign: 'center',
              transition: 'all 0.2s',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { borderColor: 'primary.light' },
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                bgcolor: 'rgba(16, 185, 129, 0.1)',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1.5,
              }}
            >
              <Upload size={22} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Drag and drop your file here
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
              or choose a file manually from your device
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button
                onClick={() => document.getElementById('upload-file-input')?.click()}
                variant="primary"
                label="Browse Files"
              />
            </Box>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
              Selected File
            </Typography>
            {uploadedFile ? (
              <Stack spacing={1}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: 'background.paper',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', wordBreak: 'break-all' }}>
                    {uploadedFile.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setUploadedFile(null)}
                    aria-label="Remove selected file"
                    sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                  >
                    <X size={16} />
                  </IconButton>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  Size: {fileSize(uploadedFile.size)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  Allowed formats: {acceptedFormats}
                </Typography>
              </Stack>
            ) : (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  border: '1px dashed',
                  borderColor: 'divider',
                  textAlign: 'center',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  No file selected yet.
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>

        <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={doUploadFile}
            variant="primary"
            label="Generate Questions"
            disabled={
              topic === undefined ||
              topic.length === 0 ||
              uploadedFile === null ||
              questionLoading ||
              (job.questions && job.questions?.length > 0) ||
              editGenerateProperties.jobTitle === ''
            }
          />
        </Box>
      </Paper>
    </Stack>
  );
};
